import React, { useState } from 'react';
import '../styles/Parser.css';

const ResumeParser = ({ onParsed, onClose }) => {
  const [mode, setMode] = useState('file'); // Default to file for resumes
  const [resumeText, setResumeText] = useState('');
  const [file, setFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(selectedFile.type)) {
        setError('Please upload a PDF, DOCX, or TXT file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleParse = async () => {
    setError('');
    setIsParsing(true);

    try {
      const token = localStorage.getItem('admin_token');
      let response;

      if (mode === 'text') {
        if (!resumeText.trim()) {
          setError('Please enter resume text');
          setIsParsing(false);
          return;
        }

        response = await fetch(`${process.env.REACT_APP_ADMIN_API_BASE}/candidates/parse-resume`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ text: resumeText })
        });
      } else {
        if (!file) {
          setError('Please select a file to upload');
          setIsParsing(false);
          return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('store_original', 'true');

        response = await fetch(`${process.env.REACT_APP_ADMIN_API_BASE}/candidates/parse-resume-file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }

      const result = await response.json();

      if (result.success) {
        setParsedData(result);
        setShowPreview(true);
      } else {
        setError(result.error || 'Failed to parse resume');
      }
    } catch (err) {
      console.error('Parse error:', err);
      setError('Failed to parse resume. Please try again.');
    } finally {
      setIsParsing(false);
    }
  };

  const handleApply = () => {
    if (parsedData && parsedData.parsed_data) {
      onParsed(parsedData.parsed_data, parsedData.fields_filled);
      onClose();
    }
  };

  const renderPreview = () => {
    if (!showPreview || !parsedData) return null;

    const { parsed_data, fields_filled } = parsedData;
    const personalInfo = parsed_data.personal_information || {};
    const education = parsed_data.education_qualifications || {};
    const workExp = parsed_data.work_experience || [];
    const techSkills = parsed_data.technical_skills || {};
    const availability = parsed_data.availability_preferences || {};

    return (
      <div className="parse-preview-modal">
        <div className="parse-preview-content">
          <h3>Parsed Resume Preview</h3>
          <p className="preview-info">
            <span className="auto-fill-badge">{fields_filled.length} fields</span> auto-filled from resume
          </p>

          <div className="preview-sections">
            <div className="preview-section">
              <h4>Personal Information</h4>
              {personalInfo.first_name && (
                <div className="preview-field">
                  <label>Name:</label>
                  <span className="auto-filled-value">
                    {personalInfo.first_name} {personalInfo.last_name}
                  </span>
                </div>
              )}
              {personalInfo.email && (
                <div className="preview-field">
                  <label>Email:</label>
                  <span className="auto-filled-value">{personalInfo.email}</span>
                </div>
              )}
              {personalInfo.phone_number && (
                <div className="preview-field">
                  <label>Phone:</label>
                  <span className="auto-filled-value">{personalInfo.phone_number}</span>
                </div>
              )}
              {personalInfo.current_location && (
                <div className="preview-field">
                  <label>Current Location:</label>
                  <span className="auto-filled-value">{personalInfo.current_location}</span>
                </div>
              )}
            </div>

            {education.degree_name && (
              <div className="preview-section">
                <h4>Education</h4>
                <div className="preview-field">
                  <label>Degree:</label>
                  <span className="auto-filled-value">
                    {education.degree_name} {education.specialization && `in ${education.specialization}`}
                  </span>
                </div>
                {education.university_name && (
                  <div className="preview-field">
                    <label>University:</label>
                    <span className="auto-filled-value">{education.university_name}</span>
                  </div>
                )}
              </div>
            )}

            {workExp.length > 0 && (
              <div className="preview-section">
                <h4>Work Experience</h4>
                {workExp.slice(0, 3).map((exp, idx) => (
                  <div key={idx} className="preview-field work-exp-item">
                    <strong>{exp.job_title}</strong> at {exp.company_name}
                    {exp.key_responsibilities && exp.key_responsibilities.length > 0 && (
                      <ul className="responsibilities-preview">
                        {exp.key_responsibilities.slice(0, 2).map((resp, i) => (
                          <li key={i}>{resp}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {workExp.length > 3 && (
                  <p className="more-indicator">+{workExp.length - 3} more positions</p>
                )}
              </div>
            )}

            {techSkills.programming_languages?.length > 0 && (
              <div className="preview-section">
                <h4>Technical Skills</h4>
                <div className="skills-preview">
                  {techSkills.programming_languages.slice(0, 10).map((skill, idx) => (
                    <span key={idx} className="skill-tag">{skill}</span>
                  ))}
                  {techSkills.programming_languages.length > 10 && (
                    <span className="more-indicator">+{techSkills.programming_languages.length - 10} more</span>
                  )}
                </div>
              </div>
            )}

            {(availability.current_salary > 0 || availability.notice_period) && (
              <div className="preview-section">
                <h4>Availability & Preferences</h4>
                {availability.current_salary > 0 && (
                  <div className="preview-field">
                    <label>Current Salary:</label>
                    <span className="auto-filled-value">${availability.current_salary.toLocaleString()}</span>
                  </div>
                )}
                {availability.expected_salary > 0 && (
                  <div className="preview-field">
                    <label>Expected Salary:</label>
                    <span className="auto-filled-value">${availability.expected_salary.toLocaleString()}</span>
                  </div>
                )}
                {availability.notice_period && (
                  <div className="preview-field">
                    <label>Notice Period:</label>
                    <span className="auto-filled-value">{availability.notice_period}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="preview-actions">
            <button className="btn-secondary" onClick={() => setShowPreview(false)}>
              Back to Edit
            </button>
            <button className="btn-primary" onClick={handleApply}>
              Apply to Form
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="resume-parser">
      <div className="parser-header">
        <h3>Parse Resume</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="parser-tabs">
        <button
          className={`tab ${mode === 'file' ? 'active' : ''}`}
          onClick={() => setMode('file')}
        >
          Upload File
        </button>
        <button
          className={`tab ${mode === 'text' ? 'active' : ''}`}
          onClick={() => setMode('text')}
        >
          Paste Text
        </button>
      </div>

      <div className="parser-content">
        {mode === 'file' ? (
          <div className="file-input-section">
            <label>Upload resume file (PDF, DOCX, or TXT):</label>
            <div className="file-upload-area">
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                onChange={handleFileChange}
                id="resume-file-input"
              />
              <label htmlFor="resume-file-input" className="file-upload-label">
                {file ? (
                  <div className="file-selected">
                    <span className="file-icon">📄</span>
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({(file.size / 1024).toFixed(2)} KB)</span>
                  </div>
                ) : (
                  <div className="file-placeholder">
                    <span className="upload-icon">📁</span>
                    <p>Click to select resume or drag and drop</p>
                    <p className="file-hint">Supported: PDF, DOCX, TXT</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        ) : (
          <div className="text-input-section">
            <label>Paste resume text below:</label>
            <textarea
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Paste the complete resume text here including personal info, education, work experience, skills, etc."
              rows={20}
              className="resume-textarea"
            />
          </div>
        )}

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="parser-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleParse}
            disabled={isParsing || (mode === 'text' && !resumeText.trim()) || (mode === 'file' && !file)}
          >
            {isParsing ? (
              <>
                <span className="spinner"></span>
                Parsing...
              </>
            ) : (
              'Parse Resume'
            )}
          </button>
        </div>
      </div>

      {renderPreview()}
    </div>
  );
};

export default ResumeParser;
